import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  FancyMultiSelect,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Label,
  Separator,
  Switch,
  Textarea,
  TimeDurationPicker,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useLocalization } from '@ais-ui/locale-management';
import { CableDetectorFormValues, useCableTriggers } from './cableForm';
import { isNonEmpty } from '@ais-ui/utils';
import { AngleInput } from '../../../../components/AngleInput';
import { usePermissions } from '../../../Permissions/PermissionsProvider';

interface CableFormProps {
  form: UseFormReturn<CableDetectorFormValues>;
}

export const CableFields = ({ form }: CableFormProps) => {
  const { messages } = useLocalization();

  const cableTriggers = useCableTriggers();

  return (
    <div className="space-y-6 px-px">
      <FormField
        control={form.control}
        name="cableTriggers"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.eventdetectorsSaveAdvancedCableTriggerLabel}
              </FormLabel>
              <button
                type="button"
                className="ml-2 text-sm text-black-500 hover:underline"
                onClick={() => field.onChange([])}
              >
                Clear All Filters
              </button>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <FancyMultiSelect
                value={field.value}
                onChange={field.onChange}
                availableSelectables={cableTriggers}
                placeholder={
                  messages.eventdetectorsSaveAdvancedCableTriggerPlaceholder
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {form
        .getValues('cableTriggers')
        .some((trigger) => trigger.value === 'detectNavStatus') && (
        <div className="space-y-4">
          <div>
            <h3 className="text-md font-medium">Navigational status</h3>
            <Separator className="mt-2"></Separator>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="navigationStatusTriggerThreshold"
              render={({ field }) => (
                <FormItem className="w-full">
                  <div className="flex justify-between">
                    <FormLabel>
                      {
                        messages.eventdetectorsSaveAdvancedNavigationalTriggerLabel
                      }
                    </FormLabel>
                  </div>
                  <FormControl onChange={() => form.clearErrors()}>
                    <Input
                      type="number"
                      placeholder={
                        messages.eventdetectorsSaveAdvancedNavigationalTriggerPlaceholder
                      }
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          // Convert the string value to a number
                          const numberValue = parseFloat(e.target.value);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="navigationStatusIgnoreThreshold"
              render={({ field }) => (
                <FormItem className="w-full">
                  <div className="flex justify-between">
                    <FormLabel>
                      {
                        messages.eventdetectorsSaveAdvancedNavigationalIgnoreLabel
                      }
                    </FormLabel>
                  </div>
                  <FormControl onChange={() => form.clearErrors()}>
                    <Input
                      type="number"
                      placeholder={
                        messages.eventdetectorsSaveAdvancedNavigationalIgnorePlaceholder
                      }
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          // Convert the string value to a number
                          const numberValue = parseFloat(e.target.value);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      )}
      {form
        .getValues('cableTriggers')
        .some((trigger) => trigger.value === 'detectDrifting') && (
        <div className="space-y-4">
          <div>
            <h3 className="text-md font-medium">Drifting</h3>
            <Separator className="mt-2"></Separator>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="driftingSpeedOverGroundThreshold"
              render={({ field }) => (
                <FormItem className="w-full">
                  <div className="flex justify-between">
                    <FormLabel>
                      {messages.eventdetectorsSaveAdvancedDriftingSpeedLabel}
                    </FormLabel>
                  </div>
                  <FormControl onChange={() => form.clearErrors()}>
                    <Input
                      type="number"
                      placeholder={
                        messages.eventdetectorsSaveAdvancedDriftingSpeedPlaceholder
                      }
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          // Convert the string value to a number
                          const numberValue = parseFloat(e.target.value);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="driftingAngleThreshold"
              render={({ field }) => (
                <FormItem className="w-full">
                  <div className="flex justify-between">
                    <FormLabel>
                      {messages.eventdetectorsSaveAdvancedDriftingAngleLabel}
                    </FormLabel>
                  </div>
                  <FormControl onChange={() => form.clearErrors()}>
                    <Input
                      type="number"
                      placeholder={
                        messages.eventdetectorsSaveAdvancedDriftingAnglePlaceholder
                      }
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          // Convert the string value to a number
                          const numberValue = parseFloat(e.target.value);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="driftingTimeThreshold"
              render={({ field }) => (
                <FormItem className="w-full">
                  <div className="flex justify-between">
                    <FormLabel>
                      {
                        messages.eventdetectorsSaveAdvancedDriftingTimeThresholdLabel
                      }
                    </FormLabel>
                  </div>
                  <FormControl onChange={() => form.clearErrors()}>
                    <Input
                      type="number"
                      placeholder={
                        messages.eventdetectorsSaveAdvancedDriftingTimeThresholdPlaceholder
                      }
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          // Convert the string value to a number
                          const numberValue = parseFloat(e.target.value);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      )}
      {form
        .getValues('cableTriggers')
        .some((trigger) => trigger.value === 'doExitCheck') && (
        <div className="space-y-4">
          <div>
            <h3 className="text-md font-medium">Exit check</h3>
            <Separator className="mt-2"></Separator>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="exitCheckThreshold"
              render={({ field }) => (
                <FormItem className="w-full">
                  <div className="flex justify-between">
                    <FormLabel>
                      {
                        messages.eventdetectorsSaveAdvancedExitCheckThresholdLabel
                      }
                    </FormLabel>
                  </div>
                  <FormControl onChange={() => form.clearErrors()}>
                    <Input
                      type="number"
                      placeholder={
                        messages.eventdetectorsSaveAdvancedExitCheckThresholdPlaceholder
                      }
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          // Convert the string value to a number
                          const numberValue = parseFloat(e.target.value);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="exitCheckTriggerLost"
              render={({ field }) => (
                <FormItem className="flex flex-col space-y-4">
                  <FormLabel>
                    {messages.eventdetectorsSaveAdvancedExitCheckTriggerLabel}
                  </FormLabel>
                  <FormControl onChange={() => form.clearErrors()}>
                    <Switch
                      checked={field.value}
                      onCheckedChange={(value) => {
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      )}
      {form
        .getValues('cableTriggers')
        .some((trigger) => trigger.value === 'detectSpeedDrop') && (
        <div className="space-y-4">
          <div>
            <h3 className="text-md font-medium">Speed drop</h3>
            <Separator className="mt-2"></Separator>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="deltaSpeedOverGroundThreshold"
              render={({ field }) => (
                <FormItem className="w-full">
                  <div className="flex justify-between">
                    <FormLabel>
                      {messages.eventdetectorsSaveAdvancedSpeedDropLabel}
                    </FormLabel>
                  </div>
                  <FormControl onChange={() => form.clearErrors()}>
                    <Input
                      type="number"
                      placeholder={
                        messages.eventdetectorsSaveAdvancedSpeedDropPlaceholder
                      }
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          // Convert the string value to a number
                          const numberValue = parseFloat(e.target.value);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const CableAdvancedForm = ({ form }: CableFormProps) => {
  const { messages } = useLocalization();
  const permissions = usePermissions();
  const [checkDirection, setCheckDirection] = React.useState(
    isNonEmpty(form.getValues('directionMinAngle')) ||
      isNonEmpty(form.getValues('directionMaxAngle')),
  );

  return (
    <div className="space-y-8">
      <Accordion type="single" collapsible>
        <AccordionItem value="advanced-text-item">
          <AccordionTrigger>Texts</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 px-px">
              <FormField
                control={form.control}
                name="activationText"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between">
                      <FormLabel>
                        {messages.eventdetectorsSaveAdvancedActivationTextLabel}
                      </FormLabel>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Textarea
                        id="eventDetectorAdvanced_activationTextField"
                        className="w-full"
                        placeholder={
                          messages.eventdetectorsSaveAdvancedActivationTextPlaceholder
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="deactivationText"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between">
                      <FormLabel>
                        {
                          messages.eventdetectorsSaveAdvancedDeactivationTextLabel
                        }
                      </FormLabel>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Textarea
                        id="eventDetectorAdvanced_deactivationTextField"
                        className="w-full"
                        placeholder={
                          messages.eventdetectorsSaveAdvancedDeactivationTextPlaceholder
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="advanced-activation-times-item">
          <AccordionTrigger>Activation times</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 px-px">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name={'timeBeforeActivation'}
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex justify-between mr-2">
                        <FormLabel>
                          {
                            messages.eventdetectorsSaveAdvancedTimeBeforeActivationLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.eventdetectorsSaveAdvancedTimeBeforeActivationTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <TimeDurationPicker
                          value={field.value}
                          onChange={field.onChange}
                          format="HH:MM:SS"
                        ></TimeDurationPicker>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={'timeBeforeDeactivation'}
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <div className="flex justify-between mr-2">
                        <FormLabel>
                          {
                            messages.eventdetectorsSaveAdvancedTimeBeforeDeactivationLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.eventdetectorsSaveAdvancedTimeBeforeDeactivationTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <TimeDurationPicker
                          value={field.value}
                          onChange={field.onChange}
                          format="HH:MM:SS"
                        ></TimeDurationPicker>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name={'timeBetweenTrigger'}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <div className="flex justify-between mr-2">
                      <FormLabel>
                        {
                          messages.eventdetectorsSaveAdvancedTimeBetweenTriggerLabel
                        }
                      </FormLabel>
                      <InfoIconTooltip>
                        {
                          messages.eventdetectorsSaveAdvancedTimeBetweenTriggerTooltip
                        }
                      </InfoIconTooltip>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <TimeDurationPicker
                        value={field.value}
                        onChange={field.onChange}
                        format="HH:MM:SS"
                      ></TimeDurationPicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={'timeoutWhenActive'}
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between mr-2">
                      <FormLabel>
                        {
                          messages.eventdetectorsSaveAdvancedTimeoutWhenActivatedLabel
                        }
                      </FormLabel>
                      <InfoIconTooltip>
                        {
                          messages.eventdetectorsSaveAdvancedTimeoutWhenActivatedTooltip
                        }
                      </InfoIconTooltip>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <TimeDurationPicker
                        value={field.value}
                        onChange={field.onChange}
                        format="HH:MM:SS"
                      ></TimeDurationPicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="advanced-direction-item">
          <AccordionTrigger>Direction</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 px-px">
              <div className="flex flex-col space-y-4">
                <div className="flex justify-between mr-2">
                  <Label>
                    {messages.eventdetectorsSaveAdvancedCheckDirectionLabel}
                  </Label>
                  <InfoIconTooltip>
                    {
                      messages.eventdetectorsSaveAdvancedCheckDirectionTooltipTextTooltip
                    }
                  </InfoIconTooltip>
                </div>
                <Switch
                  id="eventDetectorAdvanced_checkDirection"
                  checked={checkDirection}
                  onCheckedChange={(checked) => {
                    setCheckDirection(checked);
                    form.setValue('directionMinAngle', undefined);
                    form.setValue('directionMaxAngle', undefined);
                  }}
                ></Switch>
              </div>
              {checkDirection && (
                <div className="flex justify-between space-x-4">
                  <FormField
                    control={form.control}
                    name="directionMinAngle"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <div className="flex justify-between mr-2">
                          <FormLabel>
                            {
                              messages.eventdetectorsSaveAdvancedDirectionMinAngleLabel
                            }
                          </FormLabel>
                          <InfoIconTooltip>
                            {
                              messages.eventdetectorsSaveAdvancedDirectionMinAngleTooltip
                            }
                          </InfoIconTooltip>
                        </div>
                        <FormControl onChange={() => form.clearErrors()}>
                          <AngleInput
                            placeholder={
                              messages.eventdetectorsSaveAdvancedDirectionMinAnglePlaceholder
                            }
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="directionMaxAngle"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <div className="flex justify-between mr-2">
                          <FormLabel>
                            {
                              messages.eventdetectorsSaveAdvancedDirectionMaxAngleLabel
                            }
                          </FormLabel>
                          <InfoIconTooltip>
                            {
                              messages.eventdetectorsSaveAdvancedDirectionMaxAngleTooltip
                            }
                          </InfoIconTooltip>
                        </div>
                        <FormControl onChange={() => form.clearErrors()}>
                          <AngleInput
                            placeholder={
                              messages.eventdetectorsSaveAdvancedDirectionMaxAnglePlaceholder
                            }
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="advanced-vessel-trigger-item">
          <AccordionTrigger>Vessel trigger</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 px-px">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="useVesselDimensions"
                  render={({ field }) => (
                    <FormItem className="flex flex-col space-y-4">
                      <div className="flex justify-between mr-4">
                        <FormLabel>
                          {
                            messages.eventdetectorsSaveAdvancedUseVesselDimensionsLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.eventdetectorsSaveAdvancedUseVesselDimensionsTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <Switch
                          checked={field.value}
                          onCheckedChange={(value) => {
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="entireVesselInside"
                  render={({ field }) => (
                    <FormItem className="flex flex-col space-y-4">
                      <div className="flex justify-between mr-2">
                        <FormLabel>
                          {
                            messages.eventdetectorsSaveAdvancedEntireVesselInsideLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip>
                          {
                            messages.eventdetectorsSaveAdvancedEntireVesselInsideTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <Switch
                          checked={field.value}
                          onCheckedChange={(value) => {
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="advanced-notifications-item">
          <AccordionTrigger>Notifications</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 px-px">
              <div className="flex flex-col space-y-4">
                <Label>
                  {messages.eventdetectorsSaveAdvancedSendShipNotificationLabel}
                </Label>
                <div className="grid grid-cols-[auto_1fr] gap-4">
                  <FormField
                    control={form.control}
                    name="sendShipNotification"
                    render={({ field }) => (
                      <FormItem className="flex flex-col space-y-4">
                        <FormControl onChange={() => form.clearErrors()}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <Switch
                                  checked={field.value}
                                  disabled={!permissions.sendMessages}
                                  onCheckedChange={(value) => {
                                    if (permissions.sendMessages) {
                                      field.onChange(value);
                                      form.setValue('shipNotificationText', '');
                                    }
                                  }}
                                />
                              </div>
                            </TooltipTrigger>
                            {!permissions.sendMessages && (
                              <TooltipContent>
                                <p>
                                  You need permission to send messages to ships
                                </p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {form.getValues('sendShipNotification') &&
                    permissions.sendMessages && (
                      <FormField
                        control={form.control}
                        name={'shipNotificationText'}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl onChange={() => form.clearErrors()}>
                              <Textarea
                                value={field.value}
                                onChange={field.onChange}
                              ></Textarea>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
